import axios from 'axios';
import { useQuery } from 'react-query';
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout, reset } from "../../features/auth/authSlice";
import { reset as invoiceReset } from "../../features/invoice/invoiceSlice.js";
import { reset as headerReset } from "../../features/headerText/headerTextSlice.js";
import { reset as appSettingsReset } from "../../features/appSettings/appSettingsSlice.js";
import userType from '../../constants/userType.js';

// Define your query key
const USER_PROFILES_QUERY_KEY = 'userProfiles';
const USER_INVITES_QUERY_KEY = 'userInvites';
const USER_SUGGESTIONS_QUERY_KEY = 'userSuggestions';
// Create a function to fetch user profiles
export const fetchUserProfiles = async (token, dispatch, navigate, signal) => {
    try {
        const response = await axios.get('/api/user', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            signal,
        });
        return response.data;
    } catch (error) {
        if (axios.isCancel(error) || error.name === 'CanceledError') return;
        if (error.response?.data?.status === "deleted") {
            localStorage.removeItem("user");
            dispatch(logout());
            dispatch(reset());
            dispatch(invoiceReset());
            dispatch(headerReset());
            dispatch(appSettingsReset());
            toast.error('This user is deleted!', {
                position: "bottom-right",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            navigate("/");
            return;
        } else if (error.response && error.response.status === 403) {
            // Redirect to 404 page
            navigate("../../../404");
        } else if (error.response && error.response.status === 401) {
            // Redirect to login page
            navigate("/user/login");
        } else {
            console.log(error);
            navigate("../../../404");
        }
        throw new Error(error);
    }
};

export const fetchInvitations = async (token, dispatch, navigate, signal) => {
    try {
        const response = await axios.get('/api/user-invite', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            signal,
        });
        return response.data;

    } catch (error) {
        if (axios.isCancel(error) || error.name === 'CanceledError') return;
        if (error.response?.data?.status === "deleted") {
            localStorage.removeItem("user");
            dispatch(logout());
            dispatch(reset());
            dispatch(invoiceReset());
            dispatch(headerReset());
            dispatch(appSettingsReset());
            toast.error('This user is deleted!', {
                position: "bottom-right",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            navigate("/");
            return;
        } else if (error.response && error.response.status === 403) {
            // Redirect to 404 page
            navigate("../../../404");
        } else if (error.response && error.response.status === 401) {
            // Redirect to login page
            navigate("/user/login");
        } else {
            console.log(error);
            navigate("../../../404");
        }
        throw new Error(error);
    }
};


export const fetchTeams = async (token, currentPage, limit, filter, userFilter, selectedCategory, selectedAccountType, selectedStatus, selectAllTeam = false, selectAllCategory = false, selectAllAccountType = false, dispatch, navigate, signal) => {
    try {
        const response = await axios.post('/api/user-optimized', {
            page: currentPage,
            limit: limit,
            searchFilter: filter,
            userIds: userFilter,
            userCategories: selectedCategory,
            roleIds: selectedAccountType,
            isActive: selectedStatus,
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            signal,
        });
        return response.data;
    } catch (error) {
        if (axios.isCancel(error) || error.name === 'CanceledError') return;
        if (error.response?.data?.status === "deleted") {
            localStorage.removeItem("user");
            dispatch(logout());
            dispatch(reset());
            dispatch(invoiceReset());
            dispatch(headerReset());
            dispatch(appSettingsReset());
            toast.error('This user is deleted!', {
                position: "bottom-right",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            navigate("/");
            return;
        } else if (error.response && error.response.status === 403) {
            navigate("../../../404");
        } else if (error.response && error.response.status === 401) {
            navigate("/user/login");
        } else {
            console.log(error);
            navigate("../../../404");
        }
        throw new Error(error);
    }
};


export const fetchTeamsInvite = async (token, currentPage, limit, filter, selectedAccountType, selectedCategory, startDate, endDate, tz, sortBy, sortOrder, dispatch, navigate) => {
    try {
        let cancelToken;
        if (cancelToken) {
            cancelToken.cancel();
        }
        cancelToken = axios.CancelToken.source();

        const response = await axios.post('/api/user/invite/optimized', {
            page: currentPage,
            limit: limit,
            searchFilter: filter || '',
            roleIds: [], // Not used in backend, keeping for compatibility
            invite_status: Array.isArray(selectedCategory) && selectedCategory.length > 0 ? selectedCategory : [],
            type_of_account: Array.isArray(selectedAccountType) && selectedAccountType.length > 0 ? selectedAccountType : [],
            startDate: startDate || null,
            endDate: endDate || null,
            tz: tz || 'UTC',
            sort_by: sortBy || 'created_at',
            sort_order: sortOrder || 'desc',
        }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {

        if (error.response?.data?.status === "deleted") {
            localStorage.removeItem("user");
            dispatch(logout());
            dispatch(reset());
            dispatch(invoiceReset());
            dispatch(headerReset());
            dispatch(appSettingsReset());
            toast.error('This user is deleted!', {
                position: "bottom-right",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            navigate("/");
            return;
        } else if (error.response && error.response.status === 403) {
            navigate("../../../404");
        } else if (error.response && error.response.status === 401) {
            navigate("/user/login");
        } else {
            console.log(error);
            navigate("../../../404");
        }
        throw new Error(error);
    }
};

export const useUserProfilesWithPagination = (token, page, limit) => {

    return useQuery(['users-paginated', token, page, limit], async () => {
        const response = await axios.post(`/api/user-test`,
            {
                page: page,
                limit: limit
            }
            ,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

        return response.data;
    });

}


export const fetchWiseBanks = async (token, dispatch, navigate, signal) => {
    try {
        const response = await axios.get('/api/wise/banks', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            signal,
        });
        return response.data;
    } catch (error) {
        if (axios.isCancel(error) || error.name === 'CanceledError') return;
        if (error.response?.data?.status === "deleted") {
            localStorage.removeItem("user");
            dispatch(logout());
            dispatch(reset());
            dispatch(invoiceReset());
            dispatch(headerReset());
            dispatch(appSettingsReset());
            toast.error('This user is deleted!', {
                position: "bottom-right",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            navigate("/");
            return;
        } else if (error.response && error.response.status === 403) {
            // Redirect to 404 page
            navigate("../../../404");
        } else if (error.response && error.response.status === 401) {
            // Redirect to login page
            navigate("/user/login");
        } else {
            console.log(error);
            navigate("../../../404");
        }
        throw new Error(error);
    }
};


// Create a custom hook to fetch user profiles using React Query
export const useUserProfiles = (token) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    return useQuery(
        [USER_PROFILES_QUERY_KEY, token],
        ({ signal }) => fetchUserProfiles(token, dispatch, navigate, signal),
        { enabled: !!token }
    );
};

// Fetch user suggestions for calendar event attendees
export const fetchUserSuggestions = async (token, dispatch, navigate) => {
    try {
        const response = await axios.get('/api/user/suggestions', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        if (error.response?.data?.status === "deleted") {
            localStorage.removeItem("user");
            dispatch(logout());
            dispatch(reset());
            dispatch(invoiceReset());
            dispatch(headerReset());
            dispatch(appSettingsReset());
            toast.error('This user is deleted!', {
                position: "bottom-right",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            navigate("/");
            return;
        } else if (error.response && error.response.status === 403) {
            navigate("../../../404");
        } else if (error.response && error.response.status === 401) {
            navigate("/user/login");
        } else {
            console.log(error);
            navigate("../../../404");
        }
        throw new Error(error);
    }
};

export const useUserSuggestions = (token) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    return useQuery(
        USER_SUGGESTIONS_QUERY_KEY,
        () => fetchUserSuggestions(token, dispatch, navigate),
        {
            staleTime: 1000 * 60 * 5, // 5 minutes
            cacheTime: 1000 * 60 * 10, // 10 minutes
        }
    );
};

export const useTeams = (token, currentPage, limit, filter, userFilter, selectedCategory, selectedAccountType, selectedStatus) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    return useQuery(
        ['USER_TEAMS_QUERY', token, currentPage, limit, filter, userFilter, selectedCategory, selectedAccountType, selectedStatus],
        ({ signal }) => fetchTeams(token, currentPage, limit, filter, userFilter, selectedCategory, selectedAccountType, selectedStatus, false, false, false, dispatch, navigate, signal),
        {
            enabled: !!token && selectedCategory?.length > 0,
            retry: 1,
            staleTime: 0,
            refetchOnMount: true,
            refetchOnWindowFocus: true,
            keepPreviousData: true,
        }
    );
};

export const useWiseBanks = (token, enabled = true) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    return useQuery(
        ['WISE_BANKS_QUERY_KEY', token],
        ({ signal }) => fetchWiseBanks(token, dispatch, navigate, signal),
        { enabled: !!token && enabled }
    );
};

export const useTypeOfAccount = (token) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    return useQuery(USER_PROFILES_QUERY_KEY, () => fetchUserProfiles(token, dispatch, navigate));
};

export const useInvitations = (token) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    return useQuery(
        [USER_INVITES_QUERY_KEY, token],
        ({ signal }) => fetchInvitations(token, dispatch, navigate, signal)
    );
};

export const useUserPendingInvitesCount = (token) => {
    return useQuery(['userPendingInvitesCount', token], async ({ signal }) => {
        try {
            const response = await axios.get(`/api/user/get-pending-invites-count`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                signal,
            });
            return response.data.pending_invites;
        } catch (error) {
            if (axios.isCancel(error) || error.name === 'CanceledError') return;
            throw error;
        }
    }, { enabled: !!token });
};

// Fetch user by ID
// export const useUserType = (token, userRoleId) => {
//     return useQuery(
//         ['userType', userRoleId],
//         async () => {
//             const response = await axios.get(`/api/user-type`, {
//                 headers: {
//                     Authorization: `Bearer ${token}`,
//                 },
//             });

//             let filteredUserType = response.data;

//             if (userRoleId === userType.Admin) {
//                 filteredUserType = response.data.filter(
//                     (role) => role._id !== userType.Super_Admin
//                 );
//             }

//             filteredUserType = filteredUserType.filter(
//                 (role) =>
//                     role._id !== userType.Collaborator &&
//                     role._id !== userType.Business
//             );

//             return filteredUserType;
//         },
//         {
//             enabled: !!token,
//         }
//     );
// };